import { Modal, useModals } from 'almanac-ui/Modal'
import { useCallback, useMemo, useState } from 'react'
import { useMachine } from '@xstate/react'
import { brandKitCreationMachine } from 'components/BrandKitCreation/brandKitCreationMachine'
import { BrandKit, FontRole } from 'types'
import { EventId, StateId } from 'components/BrandKitCreation/consts'
import { shuffle } from 'lodash'
import { PALETTES } from 'constants/palettes'
import { BrandKitInstructions } from './BrandKitInstructions'
import { BrandKitParent } from 'flows/common/steps/BrandKitForm/BrandKitParent'
import { BrandKitColorsAndFonts } from 'flows/common/steps/BrandKitColorsAndFonts/BrandKitColorsAndFonts'
import { BrandKitImages } from 'flows/common/steps/BrandKitImages/BrandKitImages'
import { BrandKitVoice } from 'flows/common/steps/BrandKitVoice/BrandKitVoice'
import { BrandKitFinished } from 'flows/common/steps/BrandKitFinished/BrandKitFinished'
import { getBrandKitFontPairing } from 'hooks/queries/brandKits/getBrandKitFontPairing'
import { generatePath, useHistory } from 'react-router-dom'
import { BRAND_KIT_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useIsMutating } from 'hooks/useIsMutating'
import { Wizards } from 'components/Wizards/Wizards'
import Styles from './BrandKitCreationModal.module.scss'
import { ColorPaletteState, FontPairingState } from 'components/FirstTimeExperienceModal/Types'
import { ConfirmBackModal } from 'flows/common/steps/BrandKitColorsAndFonts/ConfirmBackModal/ConfirmBackModal'
import { useConfirmModal } from 'hooks/useConfirmModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { FontPairingStyle } from 'constants/fontPairings'
import { useDeleteBrandKit } from 'hooks/queries/brandKits/useDeleteBrandKit'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export const BrandKitCreationModal = () => {
  const workspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const { closeAllModals, openModal } = useModals()
  const history = useHistory()
  const useWebsiteCrawler = useFlipperFlag('website_crawler')
  const [brandKitId, setBrandKitId] = useState<number | undefined>(undefined)

  const { data } = useBrandKit(brandKitId, { includeAllMediaFiles: true })
  const brandKit = data?.brand_kit as BrandKit
  const { mutate: deleteBrandKit } = useDeleteBrandKit({ id: brandKit?.id })

  const [current, send] = useMachine(brandKitCreationMachine, {
    context: {
      brandKit: null,
    },
  })

  const [colorPaletteState, setColorPaletteState] = useState<ColorPaletteState>({
    websitePalette: [],
    generatedPalettes: [],
    selectedPaletteIndex: 0,
  })
  const [fontPairingState, setFontPairingState] = useState<FontPairingState>({
    websitePairing: null,
  })
  const [websiteAnalysisFailed, setWebsiteAnalysisFailed] = useState(false)
  const [fetchingMorePages, setFetchingMorePages] = useState(false)

  const handleBack = useCallback(() => {
    setWebsiteAnalysisFailed(false)
    send(EventId.Back)
  }, [send])
  const handleNext = useCallback(() => {
    setWebsiteAnalysisFailed(false)
    send(EventId.Next)
  }, [send])

  const backConfirm = useConfirmModal(ConfirmBackModal)

  const handleFinished = useCallback(() => {
    openModal(Wizards, { initialValues: { brand_kit_id: brandKit?.id } }, { replace: true })
  }, [openModal, brandKit?.id])

  const navigationProps = {
    onNext: handleNext,
    onBack: handleBack,
  }

  const handleBrandKitGenerated = useCallback((brandKit: BrandKit) => {
    // Save the original website palette
    const websitePalette = brandKit.brand_visual_kit?.primary_color_palette?.colors.map((color) => color.hex) || []

    // Generate random palettes at this point
    const generatedPalettes = shuffle(PALETTES).slice(0, 3)

    setColorPaletteState({
      websitePalette,
      generatedPalettes,
      selectedPaletteIndex: 0,
    })

    // Save the original website fonts
    let websitePairing = null

    if (brandKit.brand_visual_kit) {
      websitePairing = getBrandKitFontPairing(brandKit.brand_visual_kit) || null
    }

    setFontPairingState({
      websitePairing,
    })

    setBrandKitId(brandKit.id)
  }, [])

  const needsMoreImages = brandKit?.media_files && brandKit.media_files.length < 4

  const isMutating = useIsMutating({ mutationKey: 'createNewBrandKit' })

  const title = useMemo(() => {
    if (current.matches(StateId.Instructions)) {
      return 'Create your Brand Kit'
    }
    if (current.matches(StateId.Form)) {
      if (websiteAnalysisFailed) {
        return "Sorry, we couldn't analyze your website"
      }
      if (needsMoreImages && useWebsiteCrawler) {
        return 'We just need a bit more content to build your Brand Kit.'
      }
      return 'Enter your website'
    }
    if (current.matches(StateId.Colors)) {
      return "Boom! Let's confirm your new Brand Kit ✨"
    }
    if (current.matches(StateId.Images)) {
      return 'Add images and video'
    }
    if (current.matches(StateId.Voice)) {
      return 'Review your Brand Voice'
    }
    if (current.matches(StateId.Finished)) {
      return 'Bam! Here’s your finished Brand Kit!'
    }
  }, [needsMoreImages, current, websiteAnalysisFailed, useWebsiteCrawler])

  const skipConfirmBack = useMemo(() => {
    return (
      current.matches(StateId.Instructions) ||
      (current.matches(StateId.Form) && !needsMoreImages) ||
      current.matches(StateId.Finished)
    )
  }, [current, needsMoreImages])

  const handleCloseModal = async () => {
    if (skipConfirmBack) {
      closeAllModals()
      return
    }

    const confirm = await backConfirm({
      title: 'Are you sure you want to leave?',
      msg: 'Closing now will delete your current Brand Kit.',
    })

    if (confirm) {
      deleteBrandKit()
      closeAllModals()
    }
  }

  return (
    <Modal.Root size="md" blaze aria-label="Create a Brand Kit" onPressOutside={handleCloseModal}>
      {!isMutating && (isMobile || !fetchingMorePages) && <Modal.Header title={title} />}
      {current.matches(StateId.Instructions) && <BrandKitInstructions onNext={handleNext} />}
      {current.matches(StateId.Form) && (
        <BrandKitParent
          {...navigationProps}
          onBrandKitGenerated={handleBrandKitGenerated}
          withinOnboarding={false}
          useIntegrations={false}
          onWebsiteAnalysisFailed={() => setWebsiteAnalysisFailed(true)}
          onSubmit={setFetchingMorePages}
          showTitles={isMobile}
        />
      )}
      {current.matches(StateId.Colors) && !!brandKit && (
        <BrandKitColorsAndFonts
          {...navigationProps}
          brandKit={brandKit}
          colorPaletteState={colorPaletteState}
          setColorPaletteState={setColorPaletteState}
          fontPairingState={fontPairingState}
        />
      )}
      {current.matches(StateId.Images) && !!brandKit && <BrandKitImages {...navigationProps} brandKit={brandKit} />}
      {current.matches(StateId.Voice) && !!brandKit && <BrandKitVoice {...navigationProps} brandKit={brandKit} />}
      {current.matches(StateId.Finished) && !!brandKit && (
        <div className={Styles.finished}>
          <BrandKitFinished
            onBack={handleBack}
            onNext={handleFinished}
            selectedFontPairing={[
              {
                font_role: FontRole.Title,
                style: brandKit.brand_visual_kit?.text_styles?.find((style) => style.font_role === FontRole.Title)
                  ?.style,
              } as FontPairingStyle<FontRole.Title>,
              {
                font_role: FontRole.Body,
                style: brandKit.brand_visual_kit?.text_styles?.find((style) => style.font_role === FontRole.Body)
                  ?.style,
              } as FontPairingStyle<FontRole.Body>,
            ]}
            onOpenBrandKit={(brandKit) => {
              history.push(generatePath(BRAND_KIT_PATH, { id: brandKit.id, workspaceId }))
              closeAllModals()
            }}
            brandKit={brandKit}
          />
        </div>
      )}
    </Modal.Root>
  )
}
